import { formatFormatterChain, parseFormatterChain, serializeTemplateSegment } from "@imminently/decisively-core";
import { Node, ReactNodeViewRenderer, mergeAttributes } from "@tiptap/react";
import { NONE_FORMATTER } from "../../../../utils/formatterMenus";
import { AttributeTokenView } from "./AttributeTokenView";

/**
 * `attributeToken` inline node (ENG-591).
 *
 * An atom inline node that stands in for one attribute. It stores the technical
 * `attributeKey`, its `formatters` (applied in order) and an optional author-set `customLabel`;
 * the `AttributeTokenView` node view resolves the label and formatter list for
 * display. Serialises to
 * `<span data-attribute-token data-attribute-key="…" data-formatter="…" data-custom-label="…">`
 * for HTML round-trip (`data-formatter` holds the chain, `a | b`, so older single-formatter
 * HTML still loads) and to the familiar `{{ key | a | b as "label" }}` string
 * for `editor.getText()`.
 */
export interface AttributeTokenAttrs {
  attributeKey: string;
  formatters: string[];
  customLabel: string;
  /** The deprecated `@parent/...` key this token was migrated from on load. Display only, never in the template. */
  migratedFrom?: string;
}

const ATTRIBUTE_TOKEN_NAME = "attributeToken";

/** Content passed to `editor.commands.insertContent(...)` to drop a token in. */
export const attributeTokenContent = (attributeKey: string, formatters: string[] = [], customLabel = "") => ({
  type: ATTRIBUTE_TOKEN_NAME,
  attrs: { attributeKey, formatters, customLabel } satisfies AttributeTokenAttrs,
});

/**
 * A token span's formatters, from its `data-formatter` chain. The single-formatter editor
 * this was ported from wrote `data-formatter="none"` for no formatter, so stored HTML can
 * still hold it; it reads as none.
 */
export const readTokenFormatters = (element: Element): string[] =>
  parseFormatterChain(element.getAttribute("data-formatter") ?? undefined).filter(
    (formatter) => formatter !== NONE_FORMATTER.formatter,
  );

/** The `{{ }}` form of one token. */
export const attributeTokenTemplate = ({ attributeKey, formatters, customLabel }: AttributeTokenAttrs): string =>
  serializeTemplateSegment({ key: attributeKey, formatters: formatters ?? [], label: customLabel });

export const AttributeToken = Node.create({
  name: ATTRIBUTE_TOKEN_NAME,
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      attributeKey: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-attribute-key") ?? "",
        renderHTML: (attributes) => ({ "data-attribute-key": attributes.attributeKey }),
      },
      formatters: {
        default: [],
        parseHTML: readTokenFormatters,
        renderHTML: (attributes) => {
          const chain = formatFormatterChain(attributes.formatters ?? []);
          return chain ? { "data-formatter": chain } : {};
        },
      },
      customLabel: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-custom-label") ?? "",
        renderHTML: (attributes) => (attributes.customLabel ? { "data-custom-label": attributes.customLabel } : {}),
      },
      migratedFrom: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-migrated-from") ?? "",
        renderHTML: (attributes) => (attributes.migratedFrom ? { "data-migrated-from": attributes.migratedFrom } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-attribute-token]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, { "data-attribute-token": "" })];
  },

  renderText({ node }) {
    return attributeTokenTemplate(node.attrs as AttributeTokenAttrs);
  },

  addNodeView() {
    return ReactNodeViewRenderer(AttributeTokenView);
  },
});
